import { useEffect } from 'react';
import { toast } from 'sonner';

export const ISSUE_UNAVAILABLE_TOAST_MS = 2500;

/**
 * Tells the reader an issue isn't available offline, then gets out of the
 * way -- the toast equivalent of the `alert()` this replaces. Renders
 * nothing itself: like `OfflineNotice`, it's a pure bridge from `open` to
 * `sonner`'s imperative queue, rendered against the shared `<Toaster />`
 * mounted once in `app/app.tsx`.
 *
 * **Replaces** `alert('This issue is not available offline.') +
 * router.history.back()`. Same four problems as the app-wide offline
 * `alert()` this session already replaced -- see
 * `shared/ui/offline-notice/offline-notice.tsx`'s doc comment for the full
 * list.
 *
 * **Why the caller still delays its own redirect**: `alert()` was
 * synchronous, so the redirect that followed it only ran after the reader
 * dismissed the dialog -- they always saw the message before leaving. A toast
 * can't block, and `Issue.tsx` unmounts the instant `navigate()`/
 * `router.history.back()` fires, which would unmount this component (and
 * cancel the toast's own dismiss timer) before the reader has had a chance to
 * read it. So `Issue.tsx` delays the redirect by `ISSUE_UNAVAILABLE_TOAST_MS`
 * itself, rather than firing it in the same tick.
 */
export function IssueUnavailableToast({ open }: { open: boolean }) {
	useEffect(() => {
		if (!open) return;
		// Positioned apart from the connectivity toast (which defaults to
		// sonner's own bottom placement) rather than left to stack with it --
		// checked, not assumed, that they can be simultaneous: landing on an
		// issue that was never cached offline is, in the most common real
		// case, the same moment as being offline in the first place.
		const id = toast.error('This issue is not available offline.', {
			duration: ISSUE_UNAVAILABLE_TOAST_MS,
			position: 'top-center',
		});
		return () => {
			toast.dismiss(id);
		};
	}, [open]);

	return null;
}
